import { Box, ClickAwayListener } from '@mui/material';
import { BusinessForSwitcher } from '@tock/shared/components/business-switcher/BusinessSwitcherItem';
import { TextField, Typography } from 'ui';
import BusinessSwitcher from '@tock/shared/components/business-switcher/BusinessSwitcher';
import React, { useState } from 'react';

interface Props {
  amountDisplayed?: number;
  businesses: BusinessForSwitcher[];
  isDisabled?: boolean;
  label?: string;
  layout?: 'inline' | 'stacked';
  onSelectBusiness(business: BusinessForSwitcher): void;
  selectedBusiness?: BusinessForSwitcher;
}

/**
 * A compact, single-business selector backed by the business switcher search.
 *
 * The inline layout preserves the Schedule History form, while stacked is intended for
 * vertically arranged forms such as the Resy importer.
 */
const BusinessDropdownSelector: React.FC<Props> = ({
  amountDisplayed = 3,
  businesses,
  isDisabled = false,
  label = 'Business:',
  layout = 'inline',
  onSelectBusiness,
  selectedBusiness,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const isStacked = layout === 'stacked';

  const handleSelectBusiness = (business: BusinessForSwitcher) => {
    setIsSearching(false);
    onSelectBusiness(business);
  };

  return (
    <Box
      minHeight={50}
      display="flex"
      flexDirection={isStacked ? 'column' : 'row'}
      alignItems={isStacked ? 'stretch' : 'center'}
      gap={isStacked ? 1 : 0}
      width={isStacked ? '100%' : undefined}
    >
      <Box pr={isStacked ? 0 : 1.5} pl={isStacked ? 0 : 1.5}>
        <Typography variant="body1Medium">{label}</Typography>
      </Box>
      {!isSearching ? (
        <TextField
          disabled={isDisabled}
          placeholder="Select a business"
          value={selectedBusiness ? selectedBusiness.name || selectedBusiness.id : ''}
          onFocus={() => setIsSearching(true)}
          style={{ width: isStacked ? '100%' : 480 }}
        />
      ) : (
        <ClickAwayListener onClickAway={() => setIsSearching(false)}>
          <Box className="BusinessSwitcher--dropdown" width={isStacked ? '100%' : undefined}>
            <BusinessSwitcher
              isEngOrHospitality={true}
              amountDisplayed={amountDisplayed}
              businesses={businesses}
              onSelectBusiness={handleSelectBusiness}
            />
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default BusinessDropdownSelector;
